/**
 * Analytics Integration Form
 * 
 * Form for configuring Google Analytics, Search Console, and Facebook Pixel integrations
 */

import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { ArrowLeft, ExternalLink, BarChart3, Search, Facebook } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useCreateAnalyticsIntegration } from '@/features/integrations/hooks/useIntegrations'
import { AnalyticsIntegrationFormData, SyncFrequency, ReportFrequency } from '@/types/integrations'

const analyticsFormSchema = z.object({
  name: z.string().min(1, 'Integration name is required'),
  integration_type: z.enum(['google_analytics', 'google_search_console', 'facebook_pixel']),
  property_id: z.string().optional(),
  pixel_id: z.string().optional(),
  auto_import_data: z.boolean().default(true),
  track_conversions: z.boolean().default(true),
  report_frequency: z.enum(['daily', 'weekly', 'monthly', 'quarterly']),
  sync_frequency: z.enum(['real_time', 'every_15_minutes', 'hourly', 'daily', 'weekly', 'manual']),
}).refine((data) => {
  if (data.integration_type === 'facebook_pixel') {
    return !!data.pixel_id
  }
  return !!data.property_id
}, {
  message: 'Property ID or Pixel ID is required',
  path: ['property_id']
})

interface AnalyticsIntegrationFormProps {
  type: 'google_analytics' | 'google_search_console' | 'facebook_pixel'
  onSuccess: () => void
  onCancel: () => void
}

const integrationConfig = {
  google_analytics: {
    name: 'Google Analytics',
    icon: BarChart3,
    description: 'Track website traffic, user behavior, and conversions',
    propertyLabel: 'GA4 Property ID',
    propertyPlaceholder: 'G-XXXXXXXXXX',
    setupUrl: 'https://analytics.google.com',
    helpText: 'Find your GA4 Property ID in Admin > Property Settings',
  },
  google_search_console: {
    name: 'Google Search Console',
    icon: Search,
    description: 'Monitor search performance, indexing, and SEO metrics',
    propertyLabel: 'Property URL',
    propertyPlaceholder: 'https://fixmybrick.ca',
    setupUrl: 'https://search.google.com/search-console',
    helpText: 'Enter the verified property URL from Search Console',
  },
  facebook_pixel: {
    name: 'Facebook Pixel',
    icon: Facebook,
    description: 'Track conversions and build audiences for Facebook advertising',
    propertyLabel: 'Pixel ID',
    propertyPlaceholder: '1234567890123456',
    setupUrl: 'https://business.facebook.com/events_manager',
    helpText: 'Find your Pixel ID in Facebook Events Manager',
  },
}

const syncFrequencyOptions: Array<{ value: SyncFrequency; label: string }> = [
  { value: 'real_time', label: 'Real-time (recommended)' },
  { value: 'every_15_minutes', label: 'Every 15 minutes' },
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'manual', label: 'Manual only' },
]

const reportFrequencyOptions: Array<{ value: ReportFrequency; label: string }> = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly (recommended)' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
]

export function AnalyticsIntegrationForm({ type, onSuccess, onCancel }: AnalyticsIntegrationFormProps) {
  const [isConnecting, setIsConnecting] = useState(false)
  const [isConnected, setIsConnected] = useState(false)
  const createIntegration = useCreateAnalyticsIntegration()

  const config = integrationConfig[type]
  const Icon = config.icon

  const form = useForm<AnalyticsIntegrationFormData>({
    resolver: zodResolver(analyticsFormSchema),
    defaultValues: {
      name: config.name,
      integration_type: type,
      auto_import_data: true,
      track_conversions: true,
      report_frequency: 'weekly',
      sync_frequency: 'daily',
    },
  })

  const onSubmit = (data: AnalyticsIntegrationFormData) => {
    createIntegration.mutate(data, {
      onSuccess: () => {
        onSuccess()
      },
    })
  }

  const connectAccount = async () => {
    setIsConnecting(true)
    
    // Simulate OAuth/connection process
    try {
      await new Promise(resolve => setTimeout(resolve, 2000))
      
      // In real implementation, this would handle OAuth for Google services
      // or verify pixel access for Facebook
      setIsConnected(true)
      console.log(`${config.name} account connected successfully`)
    } catch (error) {
      console.error(`Failed to connect ${config.name}:`, error)
    } finally {
      setIsConnecting(false)
    }
  }

  const getFeaturesList = () => {
    switch (type) {
      case 'google_analytics':
        return [
          'Website traffic and user behavior tracking',
          'Conversion goal setup and monitoring',
          'Custom dimension and metric configuration',
          'E-commerce tracking for lead values',
          'Audience insights and demographics',
          'Automated performance reports',
        ]
      case 'google_search_console':
        return [
          'Search query performance monitoring',
          'Page indexing status and errors',
          'Core Web Vitals and site health',
          'Backlink analysis and monitoring',
          'Mobile usability insights',
          'SEO optimization recommendations',
        ]
      case 'facebook_pixel':
        return [
          'Conversion tracking for Facebook ads',
          'Custom audience building for retargeting',
          'Purchase and lead event tracking',
          'Attribution modeling and analysis',
          'Campaign optimization insights',
          'Cross-device tracking capabilities',
        ]
      default:
        return []
    }
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
      {/* Integration Overview */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Icon className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <CardTitle>{config.name} Integration</CardTitle>
              <CardDescription>{config.description}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Integration Name</Label>
            <Input
              id="name"
              {...form.register('name')}
              placeholder={config.name}
            />
            {form.formState.errors.name && (
              <p className="text-sm text-red-600">{form.formState.errors.name.message}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Account Connection */}
      <Card>
        <CardHeader>
          <CardTitle>Account Connection</CardTitle>
          <CardDescription>
            Connect your {config.name} account to enable data synchronization
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {type === 'facebook_pixel' ? (
            <div className="space-y-2">
              <Label htmlFor="pixel_id">{config.propertyLabel}</Label>
              <Input
                id="pixel_id"
                {...form.register('pixel_id')}
                placeholder={config.propertyPlaceholder}
              />
              <p className="text-xs text-muted-foreground">
                {config.helpText}
              </p>
              {form.formState.errors.property_id && (
                <p className="text-sm text-red-600">{form.formState.errors.property_id.message}</p>
              )}
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label>Google Account Connection</Label>
                {isConnected ? (
                  <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-md">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-sm text-green-700">Google account connected</span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setIsConnected(false)}
                    >
                      Disconnect
                    </Button>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={connectAccount}
                    disabled={isConnecting}
                    className="w-full"
                  >
                    {isConnecting ? 'Connecting...' : 'Connect Google Account'}
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>

              {isConnected && (
                <div className="space-y-2">
                  <Label htmlFor="property_id">{config.propertyLabel}</Label>
                  <Input
                    id="property_id"
                    {...form.register('property_id')}
                    placeholder={config.propertyPlaceholder}
                  />
                  <p className="text-xs text-muted-foreground">
                    {config.helpText}
                  </p>
                  {form.formState.errors.property_id && (
                    <p className="text-sm text-red-600">{form.formState.errors.property_id.message}</p>
                  )}
                </div>
              )}
            </>
          )}

          <Button
            type="button"
            variant="link"
            className="p-0 h-auto"
            onClick={() => window.open(config.setupUrl, '_blank')}
          >
            Need help? Visit {config.name} setup guide
            <ExternalLink className="w-3 h-3 ml-1" />
          </Button>
        </CardContent>
      </Card>

      {/* Data Collection Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Data Collection Settings</CardTitle>
          <CardDescription>
            Configure what data to collect and how often to sync
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="auto_import_data"
              checked={form.watch('auto_import_data')}
              onCheckedChange={(checked) => form.setValue('auto_import_data', !!checked)}
            />
            <Label htmlFor="auto_import_data" className="text-sm">
              Automatically import data from {config.name}
            </Label>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="track_conversions"
              checked={form.watch('track_conversions')}
              onCheckedChange={(checked) => form.setValue('track_conversions', !!checked)}
            />
            <Label htmlFor="track_conversions" className="text-sm">
              Track conversions and goal completions
            </Label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="sync_frequency">Sync Frequency</Label>
              <Select
                value={form.watch('sync_frequency')}
                onValueChange={(value) => form.setValue('sync_frequency', value as SyncFrequency)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select sync frequency" />
                </SelectTrigger>
                <SelectContent>
                  {syncFrequencyOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="report_frequency">Report Frequency</Label>
              <Select
                value={form.watch('report_frequency')}
                onValueChange={(value) => form.setValue('report_frequency', value as ReportFrequency)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select report frequency" />
                </SelectTrigger>
                <SelectContent>
                  {reportFrequencyOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Integration Features Info */}
      <Alert>
        <AlertDescription>
          <strong>What this integration will do:</strong>
          <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
            {getFeaturesList().map((feature, index) => (
              <li key={index}>{feature}</li>
            ))}
          </ul>
        </AlertDescription>
      </Alert>

      {/* Form Actions */}
      <div className="flex items-center justify-between pt-6 border-t">
        <Button type="button" variant="outline" onClick={onCancel}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button 
          type="submit" 
          disabled={createIntegration.isPending || (type !== 'facebook_pixel' && !isConnected)}
        >
          {createIntegration.isPending ? 'Creating Integration...' : 'Create Integration'}
        </Button>
      </div>
    </form>
  )
}